import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ecommerceBaseUrl, apiBaseUrl } from "network";

/**
 * 🔁 Custom baseQuery to support multiple base URLs
 */
const dynamicBaseQuery = async (args, api, extraOptions) => {
    const state = api.getState();
    const token = state.auth?.token;

    // Decide base URL
    const baseUrl = args?.useBeautyBaseUrl
        ? apiBaseUrl
        : ecommerceBaseUrl;

    const rawBaseQuery = fetchBaseQuery({
        baseUrl,
        credentials: "include",
        prepareHeaders: (headers) => {
            if (token) {
                headers.set("authorization", `Bearer ${token}`);
            }
            return headers;
        },
    });

    // Remove custom flag before request
    if (typeof args === "object") {
        const { useBeautyBaseUrl, ...rest } = args;
        return rawBaseQuery(rest, api, extraOptions);
    }

    return rawBaseQuery(args, api, extraOptions);
};

export const shopProducts = createApi({
    reducerPath: "shopProducts",
    baseQuery: dynamicBaseQuery,
    endpoints: (builder) => ({
        // ✅ Get All Products
        getProducts: builder.query({
            query: ({ page = 1, limit = 10, search = "", category = "", productType = "" }) => ({
                url: `/product`,
                method: "GET",
                params: { page, limit, search, category, productFor: productType },
            }),
        }),

        // ✅ Get Single Product
        getOneProduct: builder.query({
            query: (id) => ({
                url: `/product/${id}`,
                method: "GET",
            }),
        }),

        // ✅ Add Product
        addProducts: builder.mutation({
            query: (body) => ({
                url: "/product",
                method: "POST",
                body,
            }),
        }),

        // ✅ Update Product
        updateProducts: builder.mutation({
            query: ({ id, body }) => ({
                url: `/product/${id}`,
                method: "PUT",
                body,
            }),
        }),

        // ✅ Delete Product
        deleteProducts: builder.mutation({
            query: (id) => ({
                url: `/product/${id}`,
                method: "DELETE",
            }),
        }),

        // ✅ Category → Subcategory
        getCatBasedSubCat: builder.query({
            query: (id) => ({
                url: `/subcategory/category/${id}`,
                method: "GET",
            }),
        }),

        // ✅ Category → Brand
        getCatBasedBrand: builder.query({
            query: (id) => ({
                url: `/brand/category/${id}`,
                method: "GET",
            }),
        }),

        // ✅ SubCategory → Color
        getSubCatBasedColor: builder.query({
            query: (id) => ({
                url: `/color/subcategory/${id}`,
                method: "GET",
            }),
        }),

        // ✅ SubCategory → Size
        getSubCatBasedSize: builder.query({
            query: (id) => ({
                url: `/size/subcategory/${id}`,
                method: "GET",
            }),
        }),

        // 🌸 BEAUTICIAN PRODUCTS (DIFFERENT BASE URL)
        getBeauticianProducts: builder.query({
            query: () => ({
                url: `/v1/e-commerce/all/beauty?for=beautician`,
                method: "GET",
                useBeautyBaseUrl: true, // 👈 SWITCH BASE URL HERE
            }),
        }),

        // ✅ Add Product
        addToCart: builder.mutation({
            query: (body) => ({
                url: "/v1/cart/add",
                method: "POST",
                body,
                useBeautyBaseUrl: true, // 👈 SWITCH BASE URL HERE
            }),
        }),
        // ✅ Add Product
        RemoveFromCart: builder.mutation({
            query: (body) => ({
                url: `/v1/cart/${body.cartId}/item/${body.itemId}`,
                method: "POST",
                body,
                useBeautyBaseUrl: true, // 👈 SWITCH BASE URL HERE
            }),
        }),

        // ✅ Add Product
        getCart: builder.query({
            query: ({ userId }) => ({
                url: `/v1/cart?userId=${userId}&category=e-commerce`,
                method: "GET",
                useBeautyBaseUrl: true, // 👈 SWITCH BASE URL HERE
            }),
        }),
    }),
});

export const {
    useGetProductsQuery,
    useGetOneProductQuery,
    useAddProductsMutation,
    useUpdateProductsMutation,
    useDeleteProductsMutation,
    useGetCatBasedSubCatQuery,
    useGetCatBasedBrandQuery,
    useGetSubCatBasedColorQuery,
    useGetSubCatBasedSizeQuery,
    useGetBeauticianProductsQuery,
    useAddToCartMutation,
    useRemoveFromCartMutation,
    useGetCartQuery,
} = shopProducts;
